import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

import { apiAxios } from "@/api/config";
import { useNotification } from "@/providers/NotificationProvider";
import { useAuthStore } from "@/stores/auth";
import { useShallow } from "zustand/shallow";
import { inputClass, mutedText } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { ui, notifications } from "@/strings";
import PiCheckTickCircleStroke from "@markeroo/icons/src/stroke/general/PiCheckTickCircleStroke";

const logoUrl = new URL("../../images/logo-full.png", import.meta.url).href;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { show } = useNotification();
  const { user, hydrated } = useAuthStore(useShallow((state) => ({
    user: state.user,
    hydrated: state.hydrated,
  })));

  // Pre-fill email from navigation state (when coming from login page)
  const prefillEmail = (location.state as { email?: string } | null)?.email || "";
  const [email, setEmail] = useState(prefillEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (hydrated && user) {
      void navigate("/", { replace: true });
    }
  }, [hydrated, user, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    apiAxios
      .post("/auth/forgot-password", { email })
      .then(() => {
        setSubmitted(true);
      })
      .catch(() => {
        show(notifications.auth.passwordResetFailed, "error");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (submitted) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 py-10">
        <div className="flex w-full max-w-md flex-col items-center gap-6 rounded-2xl bg-white p-8 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
          <img src={logoUrl} alt="Markeroo logo" className="h-20 w-auto" />

          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-semantic-success-bg">
            <PiCheckTickCircleStroke className="h-6 w-6 text-semantic-success" aria-hidden="true" />
          </div>

          <h1 className="text-xl font-semibold text-text-theme-primary">
            {ui.labels.passwordResetSent}
          </h1>

          <p className={`${mutedText} text-center`}>
            {ui.labels.passwordResetInstructions}
          </p>

          <Link
            to="/login"
            className="mt-2 font-medium text-primary hover:text-primary-hover"
          >
            {ui.actions.backToLogin}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 py-10">
      <div className="flex w-full max-w-md flex-col gap-6 rounded-2xl bg-white p-8 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
        <div className="flex flex-col items-center gap-2">
          <img src={logoUrl} alt="Markeroo logo" className="h-20 w-auto" />
          <h1 className="text-xl font-semibold text-text-theme-primary">{ui.actions.forgotPassword}</h1>
          <p className={`${mutedText} text-center`}>{ui.labels.forgotPasswordInstructions}</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-theme-secondary">
              {ui.labels.email}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`${inputClass} mt-1`}
              required
              disabled={isSubmitting}
              autoComplete="email"
            />
          </div>

          <button
            type="submit"
            className={buttonVariants.primary}
            disabled={isSubmitting || !email}
          >
            {isSubmitting ? ui.actions.sendingResetLink : ui.actions.sendResetLink}
          </button>
        </form>

        <div className="text-center">
          <Link to="/login" className="text-sm font-medium text-primary hover:text-primary-hover">
            {ui.actions.backToLogin}
          </Link>
        </div>
      </div>
    </div>
  );
}
